import { useEffect, useState } from 'react';

interface ResultModalProps {
  show: boolean;
  winner: string;
  onClose: () => void;
  onSpinAgain: () => void;
  onPickDrink: () => void;
}

export default function ResultModal({ show, winner, onClose, onSpinAgain, onPickDrink }: ResultModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
    } else {
      const t = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [show]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>

      <div
        className={`relative bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 transform transition-all duration-300 ${
          show ? 'scale-100' : 'scale-90'
        }`}
      >
        {/* Decorative dots */}
        <div className="absolute inset-0 overflow-hidden rounded-3xl pointer-events-none">
          <div className="absolute top-3 left-1/4 w-2 h-2 bg-[#2349FF] rounded-full animate-ping"></div>
          <div className="absolute top-8 right-1/4 w-1.5 h-1.5 bg-[#1a38cc] rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
          <div className="absolute top-5 left-2/3 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
        </div>

        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-[#2349FF] to-[#1530b3] rounded-full flex items-center justify-center mx-auto mb-5 shadow-lg">
            <i className="ri-beer-fill text-4xl text-white"></i>
          </div>

          <h2 className="text-2xl font-extrabold text-gray-900 mb-2">We Have a Winner!</h2>

          <div className="bg-[#2349FF]/10 rounded-2xl p-5 mb-5">
            <p className="text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wider">The chosen one is...</p>
            <p className="text-3xl font-extrabold text-[#2349FF]">{winner}</p>
          </div>

          <p className="text-sm text-gray-500 mb-6">Time to buy the next round! 🍺</p>

          {/* Buttons */}
          <div className="mt-6">
            <div className="flex flex-col gap-2.5">
              <button
                onClick={() => {
                  onPickDrink();
                }}
                className="w-full px-5 py-3.5 bg-gradient-to-r from-[#2349FF] to-[#1a38cc] text-white text-sm font-bold rounded-full hover:from-[#1a38cc] hover:to-[#1530b3] transition-all whitespace-nowrap cursor-pointer shadow-lg shadow-[#2349FF]/25"
              >
                <i className="ri-goblet-fill mr-1.5"></i>Pick What Drink!
              </button>
              <button
                onClick={onClose}
                className="w-full px-5 py-3 text-gray-500 text-sm font-medium hover:text-gray-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Skip & Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
